(() => {
  'use strict';

  const root = document.documentElement;
  const liveRegion = () => document.getElementById('ThemeLiveRegion');
  const announce = (message) => {
    const region = liveRegion();
    if (!region || !message) return;
    region.textContent = '';
    requestAnimationFrame(() => { region.textContent = message; });
  };

  const getFocusable = (container) => [...container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((element) => !element.hidden && element.offsetParent !== null);

  class FocusTrap {
    constructor(container) {
      this.container = container;
      this.previous = document.activeElement;
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => getFocusable(container)[0]?.focus());
    }

    onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(this.container);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    release() {
      document.removeEventListener('keydown', this.onKeydown);
      this.previous?.focus?.();
    }
  }

  const drawers = new Map();
  const openDrawer = (drawer, trigger) => {
    if (!drawer || drawer.matches('[open], .is-open')) return;
    drawer.setAttribute('open', '');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    trigger?.setAttribute('aria-expanded', 'true');
    drawer._trigger = trigger;
    drawer._focusTrap = new FocusTrap(drawer.querySelector('[data-drawer-panel]') || drawer);
    document.body.classList.add('is-locked');
    drawers.set(drawer.id, drawer);
  };

  const closeDrawer = (drawer) => {
    if (!drawer) return;
    drawer.removeAttribute('open');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer._trigger?.setAttribute('aria-expanded', 'false');
    drawer._focusTrap?.release();
    drawer._focusTrap = null;
    drawers.delete(drawer.id);
    if (!drawers.size) document.body.classList.remove('is-locked');
  };

  const initDrawers = () => {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-drawer-open]');
      if (opener) {
        event.preventDefault();
        openDrawer(document.getElementById(opener.dataset.drawerOpen), opener);
        return;
      }
      const closer = event.target.closest('[data-drawer-close]');
      if (closer) {
        event.preventDefault();
        closeDrawer(closer.closest('[data-drawer], dialog, .drawer'));
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') [...drawers.values()].at(-1) && closeDrawer([...drawers.values()].at(-1));
    });
  };

  const initAccordions = (scope = document) => {
    scope.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
      if (trigger.dataset.initialized) return;
      trigger.dataset.initialized = 'true';
      const content = document.getElementById(trigger.getAttribute('aria-controls')) ||
        trigger.closest('[data-accordion]')?.querySelector('[data-accordion-content]');
      if (!content) return;
      trigger.setAttribute('aria-expanded', String(!content.hidden));
      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        content.hidden = expanded;
      });
    });
  };

  const updateCartCount = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
    document.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart } }));
  };

  const addToCart = async (form, button) => {
    const routes = window.theme?.routes || {};
    button?.classList.add('is-loading');
    button?.setAttribute('aria-disabled', 'true');
    try {
      const response = await fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || item.message || 'Unable to add item');
      const cartResponse = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      updateCartCount(cart);
      announce(`${item.product_title || ''} ${window.theme?.strings?.addToCart || 'added to cart'}`);
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      if (cartDrawer) {
        document.dispatchEvent(new CustomEvent('theme:cart-drawer:refresh', { detail: { cart, item } }));
        openDrawer(cartDrawer, button);
      }
    } catch (error) {
      const errorNode = form.querySelector('[data-product-form-error]');
      if (errorNode) {
        errorNode.textContent = error.message;
        errorNode.hidden = false;
      }
      announce(error.message);
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-disabled');
    }
  };

  const initQuickAdd = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-quick-add], [data-product-form]');
      if (!form || form.dataset.nativeSubmit === 'true') return;
      event.preventDefault();
      addToCart(form, form.querySelector('[type="submit"]'));
    });
  };

  const initGridToggles = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-grid-view]');
      if (!button) return;
      const target = document.querySelector(button.dataset.gridTarget || '[data-product-grid]');
      if (!target) return;
      const view = button.dataset.gridView;
      target.dataset.view = view;
      button.closest('[data-view-toggle]')?.querySelectorAll('[data-grid-view]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item === button));
      });
      try { localStorage.setItem('theme:grid-view', view); } catch (_) {}
    });
    let saved;
    try { saved = localStorage.getItem('theme:grid-view'); } catch (_) {}
    if (saved) {
      document.querySelectorAll('[data-product-grid]').forEach((grid) => { grid.dataset.view = saved; });
      document.querySelectorAll(`[data-grid-view="${saved}"]`).forEach((button) => button.setAttribute('aria-pressed', 'true'));
    }
  };

  const initStickyAtc = () => {
    const sticky = document.querySelector('[data-sticky-atc]');
    const primary = document.querySelector('[data-primary-atc]');
    if (!sticky || !primary || !('IntersectionObserver' in window)) return;
    const stickyButton = sticky.querySelector('[type="submit"], [data-sticky-atc-button]');
    const primaryForm = primary.closest('form');
    new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), {
      rootMargin: '0px 0px -15% 0px'
    }).observe(primary);
    stickyButton?.addEventListener('click', (event) => {
      if (!primaryForm) return;
      event.preventDefault();
      if (primaryForm.requestSubmit) primaryForm.requestSubmit(primary);
      else primary.click();
    });
    const variantInput = primaryForm?.querySelector('[name="id"]');
    if (variantInput) {
      const sync = () => {
        const stickyInput = sticky.querySelector('[name="id"]');
        if (stickyInput) stickyInput.value = variantInput.value;
      };
      variantInput.addEventListener('change', sync);
      sync();
    }
  };

  const initFilters = () => {
    document.addEventListener('change', (event) => {
      const form = event.target.closest('[data-filter-form]');
      if (!form || event.target.matches('[data-filter-manual]')) return;
      const url = new URL(form.action || location.href);
      new FormData(form).forEach((value, key) => { if (value) url.searchParams.append(key, value); });
      document.dispatchEvent(new CustomEvent('theme:filters:loading'));
      location.assign(url.toString());
    });
    document.addEventListener('click', (event) => {
      const clear = event.target.closest('[data-filter-clear]');
      if (!clear) return;
      event.preventDefault();
      location.assign(clear.href || clear.dataset.filterClear || location.pathname);
    });
  };

  const initLocalization = () => {
    document.addEventListener('change', (event) => {
      const control = event.target.closest('[data-localization-select]');
      if (!control) return;
      const form = control.closest('form');
      if (form?.requestSubmit) form.requestSubmit();
      else form?.submit();
    });
  };

  const initRecentlyViewed = () => {
    const product = document.querySelector('[data-product-handle]');
    if (product) {
      try {
        const handle = product.dataset.productHandle;
        const viewed = JSON.parse(localStorage.getItem('theme:recently-viewed') || '[]').filter((item) => item !== handle);
        viewed.unshift(handle);
        localStorage.setItem('theme:recently-viewed', JSON.stringify(viewed.slice(0, 8)));
      } catch (_) {}
    }
    document.querySelectorAll('[data-recently-viewed]').forEach((container) => {
      let handles = [];
      try { handles = JSON.parse(localStorage.getItem('theme:recently-viewed') || '[]'); } catch (_) {}
      handles = handles.filter(Boolean).slice(0, Number(container.dataset.limit) || 4);
      if (!handles.length) return;
      container.dispatchEvent(new CustomEvent('theme:recently-viewed:ready', { bubbles: true, detail: { handles } }));
      container.dataset.handles = handles.join(',');
    });
  };

  const initReveal = () => {
    const nodes = document.querySelectorAll('[data-reveal]');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    }), { rootMargin: '0px 0px -8% 0px' });
    nodes.forEach((node) => observer.observe(node));
  };

  const initialize = () => {
    initDrawers();
    initAccordions();
    initQuickAdd();
    initGridToggles();
    initStickyAtc();
    initFilters();
    initLocalization();
    initRecentlyViewed();
    initReveal();
    root.classList.add('theme-ready');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initAccordions(event.target);
    initReveal();
  });

  window.BMTheme = { announce, openDrawer, closeDrawer, addToCart };
})();
